import { CAMERA_MODEL_COEFFICIENT_GROUPS, coefficientsFromGroups, groupsFromCoefficients, normalizeToOfferedCameraModel, } from './cameraModels';
import { depthSettingsState } from '../state/depthSettings.svelte';
/**
 * The distortion coefficients as the panel now holds them: one input per
 * coefficient, named the way the selected model names them.
 *
 * A blank box is zero, which is what lets a calibration that gives a single
 * radial term be entered as a single number. The joined string is still the
 * shape the rest of the pipeline expects.
 */
function readCoefficientInputs(fileIndex, model) {
    const groups = CAMERA_MODEL_COEFFICIENT_GROUPS[model];
    if (!groups) {
        return '';
    }
    const entered = groups.map((_, index) => {
        const input = document.getElementById(`coefficient-group-${fileIndex}-${index}`);
        return input?.value ?? '';
    });
    return coefficientsFromGroups(model, entered).join(',');
}
/** Writes a joined coefficient string back into the grouped inputs. */
function writeCoefficientInputs(fileIndex, model, joined) {
    const groups = CAMERA_MODEL_COEFFICIENT_GROUPS[model];
    if (!groups) {
        return;
    }
    const ordered = (joined ?? '')
        .split(',')
        .map(value => Number(value.trim()))
        .map(value => (Number.isFinite(value) ? value : 0));
    const perGroup = groupsFromCoefficients(model, ordered);
    perGroup.forEach((value, index) => {
        const input = document.getElementById(`coefficient-group-${fileIndex}-${index}`);
        if (input) {
            input.value = value;
        }
    });
}
export function captureDepthPanelStates(host) {
    const states = new Map();
    // Look for all depth settings panels and capture their display state
    const panels = document.querySelectorAll('[id^="depth-panel-"]');
    panels.forEach(panel => {
        const id = panel.id;
        const match = id.match(/depth-panel-(\d+)/);
        if (match) {
            const fileIndex = parseInt(match[1]);
            // The panel's own state, not its rendered style: the component owns the
            // display now, and reading it back would race the render.
            const isVisible = depthSettingsState.openPanelIndices.includes(fileIndex);
            // Capture current form values
            const formValues = captureDepthFormValues(host, fileIndex);
            states.set(fileIndex, {
                panelOpen: isVisible,
                formValues: formValues,
            });
            console.log(`📋 Captured state for file ${fileIndex}: ${isVisible ? 'open' : 'closed'}, fx=${formValues.fx}, cx=${formValues.cx}`);
        }
    });
    return states;
}
/**
 * Capture current form values for a depth settings panel
 */
export function captureDepthFormValues(host, fileIndex) {
    const getValue = (id) => {
        const element = document.getElementById(id);
        return element ? element.value : null;
    };
    return {
        fx: getValue(`fx-${fileIndex}`),
        fy: getValue(`fy-${fileIndex}`),
        cx: getValue(`cx-${fileIndex}`),
        cy: getValue(`cy-${fileIndex}`),
        cameraModel: getValue(`camera-model-${fileIndex}`),
        depthType: getValue(`depth-type-${fileIndex}`),
        baseline: getValue(`baseline-${fileIndex}`),
        disparityOffset: getValue(`disparity-offset-${fileIndex}`),
        convention: getValue(`convention-${fileIndex}`),
        pngScaleFactor: getValue(`png-scale-factor-${fileIndex}`),
        depthScale: getValue(`depth-scale-${fileIndex}`),
        depthBias: getValue(`depth-bias-${fileIndex}`),
        k1: getValue(`k1-${fileIndex}`),
        k2: getValue(`k2-${fileIndex}`),
        k3: getValue(`k3-${fileIndex}`),
        k4: getValue(`k4-${fileIndex}`),
        k5: getValue(`k5-${fileIndex}`),
        p1: getValue(`p1-${fileIndex}`),
        p2: getValue(`p2-${fileIndex}`),
        coefficients: readCoefficientInputs(fileIndex, getValue(`camera-model-${fileIndex}`) ?? ''),
        imageRectified: document.getElementById(`image-rectified-${fileIndex}`)
            ?.checked
            ? 'true'
            : 'false',
        liveUpdate: host.liveDepthUpdateFiles.has(fileIndex) ? 'true' : 'false',
    };
}
/**
 * Restore the open/closed state of depth settings panels and form values
 */
export function restoreDepthPanelStates(host, states) {
    // Wait a bit for the DOM to be updated
    setTimeout(() => {
        // First, restore panel visibility states and form values
        states.forEach((state, fileIndex) => {
            const panel = document.getElementById(`depth-panel-${fileIndex}`);
            const toggleButton = document.querySelector(`[data-file-index="${fileIndex}"].depth-settings-toggle`);
            if (panel && toggleButton) {
                console.log(`🔄 Restoring state for file ${fileIndex}: ${state.panelOpen ? 'open' : 'closed'}`);
                // Restore panel visibility through the state the component reads, so
                // the panel and its arrow cannot end up disagreeing.
                const indices = depthSettingsState.openPanelIndices;
                const at = indices.indexOf(fileIndex);
                if (state.panelOpen && at === -1) {
                    indices.push(fileIndex);
                }
                else if (!state.panelOpen && at !== -1) {
                    indices.splice(at, 1);
                }
                // Restore form values
                restoreDepthFormValues(host, fileIndex, state.formValues);
            }
            else {
                console.warn(`⚠️ Could not find panel or toggle button for file ${fileIndex}`);
            }
        });
        // For any depth files not captured in states (edge case), restore dimensions
        host.fileDepthData.forEach((depthData, fileIndex) => {
            if (!states.has(fileIndex)) {
                const panel = document.getElementById(`depth-panel-${fileIndex}`);
                if (panel) {
                    console.log(`📐 Restoring dimensions for uncaptured file ${fileIndex}: ${depthData.depthDimensions.width}×${depthData.depthDimensions.height}`);
                    host.updatePrinciplePointFields(fileIndex, depthData.depthDimensions);
                }
            }
        });
    }, 10);
}
/**
 * Restore form values for a depth settings panel
 */
export function restoreDepthFormValues(host, fileIndex, formValues) {
    const setValue = (id, value) => {
        if (value !== null) {
            const element = document.getElementById(id);
            if (element) {
                element.value = value;
            }
        }
    };
    // Restore all captured form values
    setValue(`fx-${fileIndex}`, formValues.fx);
    setValue(`fy-${fileIndex}`, formValues.fy);
    setValue(`cx-${fileIndex}`, formValues.cx);
    setValue(`cy-${fileIndex}`, formValues.cy);
    // Settings saved before the picker was consolidated still name the specific
    // models; assigning one the select does not carry leaves it showing nothing,
    // which is what "an empty camera model is selected" looked like.
    setValue(`camera-model-${fileIndex}`, normalizeToOfferedCameraModel((formValues.cameraModel ?? '')).model);
    setValue(`depth-type-${fileIndex}`, formValues.depthType);
    setValue(`baseline-${fileIndex}`, formValues.baseline);
    setValue(`disparity-offset-${fileIndex}`, formValues.disparityOffset);
    setValue(`convention-${fileIndex}`, formValues.convention);
    setValue(`png-scale-factor-${fileIndex}`, formValues.pngScaleFactor);
    setValue(`depth-scale-${fileIndex}`, formValues.depthScale);
    setValue(`depth-bias-${fileIndex}`, formValues.depthBias);
    setValue(`k1-${fileIndex}`, formValues.k1);
    setValue(`k2-${fileIndex}`, formValues.k2);
    setValue(`k3-${fileIndex}`, formValues.k3);
    setValue(`k4-${fileIndex}`, formValues.k4);
    setValue(`k5-${fileIndex}`, formValues.k5);
    setValue(`p1-${fileIndex}`, formValues.p1);
    setValue(`p2-${fileIndex}`, formValues.p2);
    writeCoefficientInputs(fileIndex, formValues.cameraModel ?? '', formValues.coefficients ?? undefined);
    const rectifiedInput = document.getElementById(`image-rectified-${fileIndex}`);
    if (rectifiedInput) {
        rectifiedInput.checked = formValues.imageRectified === 'true';
    }
    const liveUpdate = formValues.liveUpdate === 'true';
    host.setLiveDepthUpdateEnabled(fileIndex, liveUpdate);
    const liveUpdateCheckbox = document.querySelector(`.live-depth-update[data-file-index="${fileIndex}"]`);
    if (liveUpdateCheckbox) {
        liveUpdateCheckbox.checked = liveUpdate;
    }
    // Show/hide distortion parameters based on camera model. Coefficients now
    // share one exact ordered input instead of model-specific duplicate IDs.
    const distortionGroup = document.getElementById(`distortion-params-${fileIndex}`);
    if (distortionGroup) {
        distortionGroup.style.display = [
            'pinhole-opencv',
            'fisheye-opencv',
            'fisheye-kb3',
            'fisheye624',
        ].includes(formValues.cameraModel ?? '')
            ? ''
            : 'none';
    }
    // Also ensure dimensions are displayed correctly
    const depthData = host.fileDepthData.get(fileIndex);
    if (depthData) {
        const imageSizeDiv = document.getElementById(`image-size-${fileIndex}`);
        if (imageSizeDiv) {
            imageSizeDiv.textContent = `Image Size: Width: ${depthData.depthDimensions.width}, Height: ${depthData.depthDimensions.height}`;
            console.log(`📐 Restored image size display for file ${fileIndex}: ${depthData.depthDimensions.width}×${depthData.depthDimensions.height}`);
        }
        // Backfill cx/cy if blank but dimensions are known
        const cxEl = document.getElementById(`cx-${fileIndex}`);
        const cyEl = document.getElementById(`cy-${fileIndex}`);
        const cxBlank = !cxEl?.value || cxEl.value.trim() === '';
        const cyBlank = !cyEl?.value || cyEl.value.trim() === '';
        if (cxBlank || cyBlank) {
            host.updatePrinciplePointFields(fileIndex, depthData.depthDimensions);
        }
    }
    console.log(`📝 Restored form values for file ${fileIndex}: fx=${formValues.fx}, cx=${formValues.cx}`);
}
export function getDepthSettingsFromFileUI(fileIndex) {
    console.log(`📋 getDepthSettingsFromFileUI(${fileIndex}) called`);
    const cameraModelSelect = document.getElementById(`camera-model-${fileIndex}`);
    const fxInput = document.getElementById(`fx-${fileIndex}`);
    const fyInput = document.getElementById(`fy-${fileIndex}`);
    const cxInput = document.getElementById(`cx-${fileIndex}`);
    const cyInput = document.getElementById(`cy-${fileIndex}`);
    const depthTypeSelect = document.getElementById(`depth-type-${fileIndex}`);
    const baselineInput = document.getElementById(`baseline-${fileIndex}`);
    const disparityOffsetInput = document.getElementById(`disparity-offset-${fileIndex}`);
    const depthScaleInput = document.getElementById(`depth-scale-${fileIndex}`);
    const depthBiasInput = document.getElementById(`depth-bias-${fileIndex}`);
    const conventionSelect = document.getElementById(`convention-${fileIndex}`);
    const pngScaleFactorInput = document.getElementById(`png-scale-factor-${fileIndex}`);
    const rgb24ConversionModeSelect = document.getElementById(`rgb24-conversion-mode-${fileIndex}`);
    const rgb24ScaleFactorInput = document.getElementById(`rgb24-scale-factor-${fileIndex}`);
    // Get distortion coefficient inputs
    const k1Input = document.getElementById(`k1-${fileIndex}`);
    const k2Input = document.getElementById(`k2-${fileIndex}`);
    const k3Input = document.getElementById(`k3-${fileIndex}`);
    const k4Input = document.getElementById(`k4-${fileIndex}`);
    const k5Input = document.getElementById(`k5-${fileIndex}`);
    const p1Input = document.getElementById(`p1-${fileIndex}`);
    const p2Input = document.getElementById(`p2-${fileIndex}`);
    const coefficientsJoined = readCoefficientInputs(fileIndex, document.getElementById(`camera-model-${fileIndex}`)?.value ?? '');
    const imageRectifiedInput = document.getElementById(`image-rectified-${fileIndex}`);
    const cx = cxInput?.value && cxInput.value.trim() !== '' ? parseFloat(cxInput.value) : undefined; // Will be auto-calculated if not provided
    const cy = cyInput?.value && cyInput.value.trim() !== '' ? parseFloat(cyInput.value) : undefined; // Will be auto-calculated if not provided
    const fx = parseFloat(fxInput?.value || '1000');
    const fyValue = fyInput?.value?.trim();
    const fy = fyValue && fyValue !== '' ? parseFloat(fyValue) : undefined;
    // Parse distortion coefficients (only if they have values)
    const k1 = k1Input?.value && k1Input.value.trim() !== '' ? parseFloat(k1Input.value) : undefined;
    const k2 = k2Input?.value && k2Input.value.trim() !== '' ? parseFloat(k2Input.value) : undefined;
    const k3 = k3Input?.value && k3Input.value.trim() !== '' ? parseFloat(k3Input.value) : undefined;
    const k4 = k4Input?.value && k4Input.value.trim() !== '' ? parseFloat(k4Input.value) : undefined;
    const k5 = k5Input?.value && k5Input.value.trim() !== '' ? parseFloat(k5Input.value) : undefined;
    const p1 = p1Input?.value && p1Input.value.trim() !== '' ? parseFloat(p1Input.value) : undefined;
    const p2 = p2Input?.value && p2Input.value.trim() !== '' ? parseFloat(p2Input.value) : undefined;
    const coefficients = coefficientsJoined
        ? coefficientsJoined.split(',').map(value => Number(value.trim()))
        : undefined;
    return {
        cameraModel: cameraModelSelect?.value || 'pinhole-opencv',
        fx: fx,
        fy: fy,
        cx: cx,
        cy: cy,
        depthType: depthTypeSelect?.value ||
            'euclidean',
        baseline: depthTypeSelect?.value === 'disparity'
            ? parseFloat(baselineInput?.value || '120')
            : undefined,
        disparityOffset: depthTypeSelect?.value === 'disparity'
            ? parseFloat(disparityOffsetInput?.value || '0')
            : undefined,
        depthScale: depthScaleInput?.value ? parseFloat(depthScaleInput.value) : undefined,
        depthBias: depthBiasInput?.value ? parseFloat(depthBiasInput.value) : undefined,
        convention: conventionSelect?.value || 'opengl',
        pngScaleFactor: pngScaleFactorInput
            ? parseFloat(pngScaleFactorInput.value || '1000') || 1000
            : undefined,
        rgb24ConversionMode: rgb24ConversionModeSelect?.value ||
            'shift',
        rgb24ScaleFactor: rgb24ScaleFactorInput
            ? parseFloat(rgb24ScaleFactorInput.value || '1000') || 1000
            : undefined,
        k1: k1,
        k2: k2,
        k3: k3,
        k4: k4,
        k5: k5,
        p1: p1,
        p2: p2,
        coefficients,
        imageRectified: imageRectifiedInput?.checked || false,
    };
}
